/**
 * Конфигурация API клиента
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import { chatLogger } from '@/utils/chatLogger';

/**
 * Конфигурация API
 */
export interface ApiConfig {
  /** Базовый URL API */
  baseURL: string;
  /** API ключ */
  apiKey: string;
  /** Timeout для запросов (мс) */
  timeout?: number;
  /** Debug режим */
  debug?: boolean;
}

/**
 * Создает настроенный Axios instance
 * 
 * @param config - конфигурация API
 * @returns настроенный Axios instance
 */
export function createApiClient(config: ApiConfig): AxiosInstance {
  const client = axios.create({
    baseURL: config.baseURL,
    timeout: config.timeout || 30000,
    headers: {
      'Authorization': `Bearer ${config.apiKey}`,
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor (для логирования в debug режиме)
  client.interceptors.request.use(
    (requestConfig) => {
      if (config.debug) {
        chatLogger.log('[API Request]', {
          method: requestConfig.method?.toUpperCase(),
          url: requestConfig.url,
          data: requestConfig.data,
        });
      }
      return requestConfig;
    },
    (error) => {
      if (config.debug) {
        chatLogger.error('[API Request Error]', error);
      }
      return Promise.reject(error);
    }
  );

  // Response interceptor (для обработки ошибок)
  client.interceptors.response.use(
    (response) => {
      if (config.debug) {
        chatLogger.log('[API Response]', {
          status: response.status,
          data: response.data,
        });
      }
      return response;
    },
    (error: AxiosError) => {
      if (config.debug) {
        chatLogger.error('[API Response Error]', {
          status: error.response?.status,
          message: error.message,
          data: error.response?.data,
        });
      }

      // Обработка различных типов ошибок
      if (error.response) {
        // Ошибка от сервера (4xx, 5xx)
        const status = error.response.status;
        const data = error.response.data as { code?: string; message?: string };

        throw {
          status,
          code: data.code || 'unknown_error',
          message: data.message || error.message,
          original: error,
        };
      } else if (error.request) {
        // Запрос был отправлен, но ответа не получено (сетевая ошибка)
        throw {
          status: 0,
          code: 'network_error',
          message: 'Проблема с подключением. Проверьте интернет.',
          original: error,
        };
      } else {
        // Ошибка при настройке запроса
        throw {
          status: 0,
          code: 'request_error',
          message: error.message,
          original: error,
        };
      }
    }
  );

  return client;
}

/**
 * Кастомный тип ошибки API
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code: string,
    public original?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

